import { derived, get, writable } from 'svelte/store'

import { errors } from '$lib/stores/errors'

import { SaveStatus } from './functions/formatters'

export enum SemaphoreKey {
	Account = 'account',
	Client = 'client',
	ClientShippingLocation = 'client_shipping_location',

	Invoice = 'invoice',
	InvoiceShippingItem = 'invoice_shipping_item',
	Payment = 'payment',

	Estimate = 'estimate',
	EstimateShippingItem = 'estimate_shipping_item',

	Expense = 'expense',
	ExpenseCategory = 'expense_category',

	Category = 'category',
	Subcategory = 'subcategory',
	Item = 'item',
	ShippingItem = 'shipping_item',
	Tax = 'tax',
	ItemTax = 'item_tax',

	OpenaiThreadItem = 'openai_thread_item',
	OpenaiExamples = 'openai_examples',

	User = 'user',
	Users = 'users',

	AccountRole = 'account_role',
	AccountRoles = 'account_roles',
	
	Total = 'total'
}

export type Semaphores = Record<SemaphoreKey, number | null>
type RESOLVE = (value?: object | PromiseLike<object>) => void
type REJECT = (reason?: unknown) => void

function defaultSemaphores(): Semaphores {
	return {
		account: null,
		client: null,
		client_shipping_location: null,
		invoice: null,
		invoice_shipping_item: null,
		payment: null,
		estimate: null,
		estimate_shipping_item: null,
		expense: null,
		category: null,
		subcategory: null,
		item: null,
		shipping_item: null,
		tax: null,
		item_tax: null,
		total: null
	}
}

// const DEFAULT_TIMEOUT = 200
// const DEFAULT_TIMEOUT = 500
const DEFAULT_TIMEOUT = 1000
// const DEFAULT_TIMEOUT = 3000
// const DEFAULT_TIMEOUT = 10000

//semaphores
const store = writable(defaultSemaphores())
const timers: Record<string, number> = {}
const promises: Record<string, [RESOLVE, REJECT][]> = {}

export function incrementSemaphore(key: SemaphoreKey) {
	if (!key) {
		throw new Error('Must specify key')
	}
	store.update((existing) => ({
		...existing,
		total: (existing.total || 0) + 1,
		[key]: (existing[key] || 0) + 1
	}))
}

export function decrementSemaphore(key: SemaphoreKey) {
	if (!key) {
		throw new Error('Must specify key')
	}
	store.update((existing) => ({
		...existing,
		total: (existing.total || 0) - 1,
		[key]: (existing[key] || 0) - 1
	}))
}

export function resetAllSemaphores() {
	store.set(defaultSemaphores())
}

export function resetSemaphore(key: string) {
	if (!key) {
		throw new Error('Must specify key')
	}
	store.update((existing) => ({ ...existing, [key]: null }))
}

// export async function debounced(key: SemaphoreKey, fn: () => object, timeout = DEFAULT_TIMEOUT): Promise<unknown> {	// Save the promise that will be returned
export async function debounced(key: SemaphoreKey, fn: () => object, timeout = DEFAULT_TIMEOUT): Promise<Promise<unknown>> {
	// TODO can we please remove/fix this a lot
	let resolve: RESOLVE = () => {}
	let reject: REJECT = () => {}

	const promise = new Promise((_resolve, _reject) => {
		resolve = _resolve
		reject = _reject
	})

	promises[key] = (promises[key] || []).concat([[resolve, reject]])

	if (timers[key]) {
		// decrementSemaphore(key);
		clearTimeout(timers[key])
		timers[key] = 0
	} else {
		incrementSemaphore(key)
	}

	// Wait for timeout ms before executing, alerting all promises awaiting
	timers[key] = window.setTimeout(async () => {
		timers[key] = 0
		let result: object
		try {
			result = await fn()

			promises[key].forEach(([resolve]) => {
				resolve(result)
			})
		} catch (err) {
			promises[key].forEach(([, reject]) => {
				reject(err)
			})
		} finally {
			promises[key] = []
			decrementSemaphore(key)
		}
	}, timeout)

	return promise
	// return await promise
}

// Wait For Saving process
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))
export async function waitDebouncedFinish(key: SemaphoreKey) {
	while (get(store)[key] && get(store)[key] !== 0) {
		console.log('semaphore - waitDeboucedFinish - sleep()', key)
		await sleep(100)
	}
	return
}

function simpleStatus(semaphore: number | null) {
	if (semaphore === null) {
		return SaveStatus.Draft
	} else if (semaphore === 0) {
		return SaveStatus.Saved
	} else {
		return SaveStatus.Saving
	}
}

const itemSaveStatus = derived(store, ($store) => simpleStatus($store.item))

const clientSaveStatus = derived(store, ($store) => simpleStatus($store.client))

const clientShippingLocationSaveStatus = derived(store, ($store) => simpleStatus($store.client_shipping_location))

const accountSaveStatus = derived(store, ($store) => simpleStatus($store.account))

const invoiceSaveStatus = derived([store, errors], ([$store, $errors]): SaveStatus => {
	if (Object.values($errors).some((val) => !!val)) {
		return SaveStatus.Error
	}

	if (
		$store.account === null &&
		$store.client === null &&
		$store.invoice === null &&
		$store.invoice_shipping_item === null &&
		$store.payment === null &&
		$store.tax === null
	) {
		return SaveStatus.Draft
	}
	// ok, no errors, and at least one is saving
	const counter =
		($store.account || 0) +
		($store.client || 0) +
		($store.invoice || 0) +
		($store.invoice_shipping_item || 0) +
		($store.payment || 0) +
		($store.tax || 0)
	if (counter === 0) {
		return SaveStatus.Saved
	} else if (counter < 0) {
		// this condition should never happen
		console.error('semaphore counter <0')
		resetAllSemaphores()
		return SaveStatus.Saved
	}
	// console.log('beforeNavigate - semaphores - derived status', counter)

	return SaveStatus.Saving
})


const estimateSaveStatus = derived([store, errors], ([$store, $errors]): SaveStatus => {
	if (Object.values($errors).some((val) => !!val)) {
		return SaveStatus.Error
	}
	if ($store.account === null && 
		$store.client === null && 
		$store.estimate === null) 
	{
		return SaveStatus.Draft
	}
	// ok, no errors, and at least one is saving
	const counter =
		($store.account || 0) +
		($store.client || 0) +
		($store.estimate || 0)
	if (counter === 0)
	{
		return SaveStatus.Saved
	}

	console.log('beforeNavigate - semaphores - derived status', counter)

	return SaveStatus.Saving
})

const openaiExamplesSaveStatus = derived(store, ($store) => simpleStatus($store.openai_examples))

export {
	accountSaveStatus,
	clientSaveStatus,
	clientShippingLocationSaveStatus,
	estimateSaveStatus,
	invoiceSaveStatus,
	itemSaveStatus,
	openaiExamplesSaveStatus,
	store as semaphores
}
